import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import SearchChrome from './search/SearchChrome';
import PeopleConnectionRow from './people/PeopleConnectionRow';
import PeopleListEmptyState from './people/PeopleListEmptyState';
import { peopleListSurfaceClass } from '../config/glassSurfaces';
import { isSearchInFlight, shouldShowSearchEmpty } from '../utils/searchUiState';
import { filterPeopleByName } from '../utils/peopleSearchFilter';
import { NOTIFICATION_TYPES } from '../utils/notifications';
import { invalidateAfterFollow } from '../lib/query/invalidateMutations';
import { useFollowingForUser, useSearchPeopleRanked } from '../hooks/queries/usePeopleQueries';

export default function PeopleSearch() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { session, authReady } = useCurrentUserProfile();
  const currentUserId = session?.user?.id;
  const peopleQueriesEnabled = authReady && Boolean(currentUserId);

  const queryFromUrl = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(queryFromUrl);
  const [debouncedQuery, setDebouncedQuery] = useState(queryFromUrl.trim());
  const [optimisticFollowing, setOptimisticFollowing] = useState(new Set());
  const [followLoadingId, setFollowLoadingId] = useState(null);

  const searchInputRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 640px)');
    const redirectDesktop = () => {
      if (mq.matches) navigate('/profile/following', { replace: true });
    };
    redirectDesktop();
    mq.addEventListener('change', redirectDesktop);
    return () => mq.removeEventListener('change', redirectDesktop);
  }, [navigate]);

  useEffect(() => {
    setSearchInput(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const current = searchParams.get('q') ?? '';
    if (trimmed === current) return;
    const next = new URLSearchParams(searchParams);
    if (trimmed) next.set('q', trimmed);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }, [searchInput, searchParams, setSearchParams]);

  const { data: myFollowing = [] } = useFollowingForUser(currentUserId, {
    enabled: peopleQueriesEnabled,
  });
  const followingIds = useMemo(
    () => new Set([...myFollowing.map((p) => p.id), ...optimisticFollowing]),
    [myFollowing, optimisticFollowing],
  );

  const {
    data: rawResults = [],
    isPending,
    isFetching,
  } = useSearchPeopleRanked(currentUserId, debouncedQuery, {
    enabled: peopleQueriesEnabled && Boolean(debouncedQuery),
  });

  const results = useMemo(
    () => filterPeopleByName(rawResults, debouncedQuery),
    [rawResults, debouncedQuery],
  );

  const searchInFlight = isSearchInFlight({
    input: searchInput,
    debouncedQuery,
    isLoading: isPending,
    isFetching,
  });
  const showNoResults = shouldShowSearchEmpty({
    input: searchInput,
    debouncedQuery,
    isLoading: isPending,
    isFetching,
    resultCount: results.length,
  });

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/profile/following');
    }
  }, [navigate]);

  const handleFollow = async (userId) => {
    if (!currentUserId || followLoadingId) return;
    setFollowLoadingId(userId);
    setOptimisticFollowing((prev) => new Set(prev).add(userId));

    try {
      const { error } = await supabase.from('follows').insert({
        follower_id: currentUserId,
        following_id: userId,
      });
      if (error) throw error;

      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', currentUserId)
        .maybeSingle();
      const displayName = profile?.display_name?.trim() || 'Someone';

      await supabase.from('notifications').insert({
        user_id: userId,
        type: NOTIFICATION_TYPES.FOLLOW,
        title: 'New follower',
        message: `${displayName} started following you`,
        related_user_id: currentUserId,
        read: false,
        created_at: new Date().toISOString(),
      });
      invalidateAfterFollow(queryClient, { viewerId: currentUserId, targetUserId: userId });
    } catch (err) {
      console.error('[PeopleSearch] Error following user:', err);
    } finally {
      setFollowLoadingId(null);
      setOptimisticFollowing((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  if (!session) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <SearchChrome
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          searchInputRef={searchInputRef}
          onBack={handleBack}
          placeholder="Find someone"
          backLabel="Back to Following"
          onClear={() => {
            setSearchInput('');
            searchInputRef.current?.focus();
          }}
        />
        <p className="mt-8 text-center text-gray-500 dark:text-dark-muted">
          Log in to search for people.
        </p>
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SearchChrome
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          searchInputRef={searchInputRef}
          onBack={handleBack}
          placeholder="Find someone"
          backLabel="Back to Following"
          onClear={() => {
            setSearchInput('');
            searchInputRef.current?.focus();
          }}
        />

        <div className="mt-4">
          {!searchInput.trim() ? (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-dark-muted">
              <p className="text-base">Start typing to find someone.</p>
            </div>
          ) : searchInFlight && results.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-dark-muted py-12">Searching...</p>
          ) : showNoResults ? (
            <PeopleListEmptyState>
              No one matches &quot;{debouncedQuery}&quot;.
            </PeopleListEmptyState>
          ) : (
            <div className={peopleListSurfaceClass()}>
              {results.map((person) => (
                <PeopleConnectionRow
                  key={person.id}
                  profile={person}
                  isFollowing={followingIds.has(person.id)}
                  isLoading={followLoadingId === person.id}
                  onFollow={() => handleFollow(person.id)}
                  onNavigate={() => navigate(`/profile/${person.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
